import React, { useCallback, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMediaQuery } from 'react-responsive';
import styled from '@emotion/styled';

import { ARENA_SELECTED, EVENT_SELECTED } from '@/constants/apiUrls';
import { RequiredMark } from '@/components/input/InputBasic';
import { ExtraBoldText, RegularText } from '@/components/text';
import { useGetBusinessCategory } from '@/hooks/CommonRequests';
import { EventForm } from '@/pages/event';
import { ArenaForm } from '@/pages/arena';
import { InquirySteps, InquiryStepOne } from '@/pages/inquiry';
import { zodEventType } from '@/utils/zodValidation';
import { Container, InputForm } from './inquryStyles';

const TextWrapper = styled.div(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '2rem 0',
  marginBottom: '2rem',
  whiteSpace: 'break-spaces',
  textAlign: 'center',
}));

const Wrapper = styled.div(() => ({
  width: '100%',
  maxWidth: '58rem',
  margin: '0 auto',
}));

const MandatoryFields = styled.div(() => ({
  width: '100%',
  marginBottom: '1.4rem',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const InquiryFormPage = () => {
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const { type } = useParams();
  const { data: businessCategory } = useGetBusinessCategory();

  const [step, setStep] = useState(0);
  const [eventType, setEventType] = useState('0');

  const currentCategory = businessCategory?.find((cat) => cat.code_id === type);
  const businessKey =
    currentCategory?.code_value || (type === 'arena' ? ARENA_SELECTED : EVENT_SELECTED);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(zodEventType),
    defaultValues: {
      business_key: businessKey,
      event_type_key: '1',
      termsAll: false,
      termsReq1: false,
      termsReq2: false,
    },
  });

  useEffect(() => {
    if (currentCategory) {
      setValue('business_key', currentCategory.code_value);
    }
  }, [currentCategory, setValue]);

  const onTypeSubmit = useCallback(async (data) => {
    setEventType(data.event_type_key);
    setStep((prev) => prev + 1);
  }, []);

  return (
    <Container>
      <TextWrapper>
        <ExtraBoldText id="lang-event-inquiry" fontSize={isMobile ? '3.6rem' : '4.8rem'} />
        <RegularText id="lang-inquiry-description" secondary />
      </TextWrapper>

      {(step === 1 || step === 2) && <InquirySteps step={step} />}

      <Wrapper>
        <MandatoryFields>
          <RegularText fontSize="1.6rem">
            <RequiredMark>*</RequiredMark>{' '}
            <RegularText id="lang-mandatory-fields" fontSize="1.6rem" />
          </RegularText>
        </MandatoryFields>

        {step === 0 && (
          <InputForm onSubmit={handleSubmit(onTypeSubmit)}>
            <InquiryStepOne control={control} errors={errors} watch={watch} setValue={setValue} />
          </InputForm>
        )}

        {businessKey === ARENA_SELECTED && (
          <ArenaForm step={step} setStep={setStep} eventType={eventType} />
        )}

        {businessKey === EVENT_SELECTED && (
          <EventForm step={step} setStep={setStep} eventType={eventType} />
        )}
      </Wrapper>
    </Container>
  );
};

export default InquiryFormPage;
